import emailjs from "@emailjs/browser";
import { Canvas } from "@react-three/fiber";
import { Suspense, useRef, useState } from "react";
import { db, collection, addDoc } from "../firebase";

import { Fox } from "./Fox.jsx";
import useAlert from "../hooks/useAlert";
import  Alert from "./Alert";
import  Loader  from "./Loader";

const Contact = () => {
  const formRef = useRef();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const { alert, showAlert, hideAlert } = useAlert();
  const [loading, setLoading] = useState(false);
  const [currentAnimation, setCurrentAnimation] = useState("idle");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm({ ...form, [name]: value });
    
    // Clear email error when user starts typing
    if (name === "email" && emailError) {
      setEmailError("");
    }
  };

  const handleFocus = () => setCurrentAnimation("walk");
  const handleBlur = () => setCurrentAnimation("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setCurrentAnimation("hit");

    // Validate email format
    if (!validateEmail(form.email)) {
      setEmailError("Please enter a valid email address");
      setLoading(false);
      setCurrentAnimation("idle");
      showAlert({
        show: true,
        text: "Please enter a valid email address",
        type: "danger",
      });
      return;
    }

    try {
      await emailjs.send(
        import.meta.env.VITE_APP_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_APP_EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          to_name: "Avansh Yadav",
          to_email: "avanshyadav20072006@gmail.com",
          message: form.message,
          reply_to: form.email,
          subject: `New Portfolio Contact from ${form.name}`,
        },
        import.meta.env.VITE_APP_EMAILJS_PUBLIC_KEY
      );

      try {
        await addDoc(collection(db, 'submissions'), {
          name: form.name,
          email: form.email,
          message: form.message,
          timestamp: new Date().toISOString(),
          status: 'unread',
          source: 'contact_form'
        });
      } catch (dbError) {
        console.warn('Failed to save to database:', dbError);
      }

      setLoading(false);
      showAlert({
        show: true,
        text: "Thanks for reaching out!",
        type: "success",
      });

      setTimeout(() => {
        hideAlert(false);
        setCurrentAnimation("idle");
        setForm({ name: "", email: "", message: "" });
      }, 3000);
    } catch (error) {
      setLoading(false);
      console.error(error);
      setCurrentAnimation("idle");
      showAlert({
        show: true,
        text: "Something went wrong. Please try again.",
        type: "danger",
      });
    }
  };

  return (
    <section className="relative flex flex-col gap-8 px-4 py-12 mx-auto overflow-hidden text-white bg-[#1d1836] sm:px-6 lg:flex-row max-w-7xl lg:px-16">
    {alert.show && <Alert {...alert} />}
  
    <div className="flex flex-col flex-1 w-full min-w-0 lg:min-w-[50%]">
      <h1 className="text-3xl font-bold text-center text-gray-100 lg:text-4xl lg:text-left">Get in Touch</h1>
  
      <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col w-full gap-7 mt-10 sm:mt-14">
        <label className="font-semibold text-gray-300">
          Name
          <input
            type="text"
            name="name"
            className="w-full px-4 py-3 mt-2 text-white transition duration-300 bg-gray-800 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Your Name"
            required
            value={form.name}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </label>
  
        <label className="font-semibold text-gray-300">
          Email
          <input
            type="email"
            name="email"
            className={`w-full px-4 py-3 mt-2 text-white transition duration-300 bg-gray-800 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 ${
              emailError ? 'border-red-500 focus:ring-red-400' : 'border-gray-600'
            }`}
            placeholder="your.email@example.com"
            required
            value={form.email}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {emailError && <span className="text-sm text-red-500 mt-1">{emailError}</span>}
        </label>
  
        <label className="font-semibold text-gray-300">
          Your Message
          <textarea
            name="message"
            rows="4"
            className="w-full px-4 py-3 mt-2 text-white transition duration-300 bg-gray-800 border border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Write your thoughts here..."
            value={form.message}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </label>
  
        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 font-semibold text-white transition duration-300 bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-blue-500/50"
          onFocus={handleFocus}
          onBlur={handleBlur}
        >
          {loading ? "Sending..." : "Submit"}
        </button>
      </form>
    </div>
  
    <div className="lg:w-1/2 w-full min-w-0 lg:h-auto md:h-[550px] h-[320px] min-[420px]:h-[350px]">
      <Canvas
        camera={{
          position: [0, 0, 5],
          fov: 75,
          near: 0.1,
          far: 1000,
        }}
      >
        <directionalLight position={[0, 0, 1]} intensity={2.5} />
        <ambientLight intensity={1} />
        <pointLight position={[5, 10, 0]} intensity={2} />
        <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={2} />
  
        <Suspense fallback={<Loader />}>
          <Fox
            currentAnimation={currentAnimation}
            position={[0.5, 0.35, 0]}
            rotation={[12.629, -0.6, 0]}
            scale={[0.5, 0.5, 0.5]}
          />
        </Suspense>
      </Canvas>
    </div>
  </section>
  
  );
};

export default Contact;
